"use client";

import { useState, useEffect } from "react";
import MasterLayout from "../Sections/MasterLayout";
import {
  server_post_data,
  save_update_line,
  get_all_lines,
  delete_line
} from "../../ServiceConnection/serviceconnection";
import { handleLinkClick } from "../../CommonJquery/CommonJquery";
import Loader from "./Loader";
import SuccesModal from "../Sections/SuccesModal";
import { useNavigate } from "react-router-dom";

export default function AddLine() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lines, setLines] = useState([]);
  const [editLineId, setEditLineId] = useState(null); // Track the ID of the line being edited
  const [formData, setFormData] = useState({
    lineName: "",
    color: "#3B82F6", // Default blue color
  });
  const [errors, setErrors] = useState({});

  // Fetch existing lines on component mount
  useEffect(() => {
    fetchLines();
  }, []);

  const fetchLines = async () => {
    try {
      setIsLoading(true);
      let fd = new FormData();
      fd.append("city_id", "4");
      const response = await server_post_data(get_all_lines, fd);
      console.log("Fetched lines:", response.data.data);
      setLines(response.data.data || []);
    } catch (error) {
      console.error("Error fetching lines:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit button click to populate form with line data
  const handleEditLine = (line) => {
    setFormData({
      lineName: line.name || "",
      color: line.color || "#3B82F6",
    });
    setEditLineId(line.id || line.primary_id); // Use primary_id if id is not available
    setErrors({}); // Clear any existing errors
    console.log("Editing line:", line);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validate = () => {
    const newErrors = {};
    const { lineName, color } = formData;

    if (!lineName.trim()) newErrors.lineName = "Line Name is required";
    if (!color) newErrors.color = "Color is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    const fd = new FormData();
    fd.append("name", formData.lineName);
    fd.append("table_suffix", formData.lineName + "_line");
    fd.append("color", formData.color);
    fd.append("city_id", "4");
    if (editLineId) {
      fd.append("line_id", editLineId); // Include line_id for update
    }

    try {
      await server_post_data(save_update_line, fd);
      setShowSuccessModal(true);
      setFormData({
        lineName: "",
        color: "#3B82F6",
      });
      setEditLineId(null); // Reset edit mode
      setErrors({});
      // Refresh the lines list
      await fetchLines();
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);
    } catch (error) {
      console.error("Error saving/updating line:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (window.confirm("Are you sure you want to delete this line?")) {
      try {
        setIsLoading(true);
        let fd = new FormData();
        fd.append("line_id", lineId)
        await server_post_data(delete_line, fd);
        await fetchLines(); // Refresh the list
      } catch (error) {
        console.error("Error deleting line:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <MasterLayout>
      {isLoading && <Loader />}

      {/* Add/Edit Line Section */}
      <div className="max-w-5xl bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6">
        <h2 className="text-xl font-semibold mb-4">
          {editLineId ? "Edit Metro Line" : "Add Metro Line"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Line Name
              </label>
              <input
                type="text"
                name="lineName"
                value={formData.lineName}
                onChange={handleChange}
                placeholder="e.g., Red Line, Blue Line"
                className={`w-full border px-3 py-2 rounded-md ${
                  errors.lineName ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.lineName && (
                <p className="text-sm text-red-500">{errors.lineName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Line Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  className={`w-16 h-10 border rounded-md cursor-pointer ${
                    errors.color ? "border-red-500" : "border-gray-300"
                  }`}
                />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, color: e.target.value }))
                  }
                  placeholder="#3B82F6"
                  className={`flex-1 border px-3 py-2 rounded-md ${
                    errors.color ? "border-red-500" : "border-gray-300"
                  }`}
                />
              </div>
              {errors.color && (
                <p className="text-sm text-red-500">{errors.color}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-6 gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded-md btn-secoundary"
              onClick={() => {
                setFormData({ lineName: "", color: "#3B82F6" });
                setEditLineId(null); // Reset edit mode
                setErrors({});
                navigate(-1);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 bg-[var(--primary-color)] text-white px-4 py-2 rounded-md hover:bg-[var(--primary-hover)] transition duration-200"
            >
              {editLineId ? "Update Line" : "Add Line"}
            </button>
          </div>
        </form>
      </div>

      {/* Lines List Section */}
      <div className="max-w-5xl bg-white p-8 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold mb-4">Metro Lines</h2>

        {lines.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No metro lines added yet.</p>
            <p className="text-sm">Add your first line using the form above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Line Name
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Color
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Preview
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr
                    key={line.id || line.primary_id || index}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4 font-medium">
                      {line.name || line.lineName}
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                        {line.color}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded border border-gray-300"
                          style={{ backgroundColor: line.color }}
                        ></div>
                        <div
                          className="h-1 w-16 rounded"
                          style={{ backgroundColor: line.color }}
                        ></div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            handleLinkClick(`/routes/${line.primary_id}`);
                          }}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Routes
                        </button>
                        <button
                          onClick={() => handleEditLine(line)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteLine(line.id || line.primary_id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {lines.length > 0 && (
          <div className="mt-4 text-sm text-gray-600">
            Total Lines: {lines.length}
          </div>
        )}
      </div>

      <SuccesModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={editLineId ? "Line Updated Successfully!" : "Line Added Successfully!"}
        subtitle="Your metro line has been saved and updated in the system."
        autoClose={false}
      />
    </MasterLayout>
  );
}